import React, { useContext, useMemo } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { Table } from 'semantic-ui-react';

import '../css/StudentAssignmentListCard.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ASSIGNMENT_MENU_STATUS } from '../managers/AssignmentManager';
import { DIALOG_NAMES } from '../managers/DialogManager';

import ImageService from '../services/ImageService';
import StudentAssignmentService from '../services/StudentAssignmentService';
import UtilityService from '../services/UtilityService';

const StudentAssignmentListCard = observer((props) => {
  const { additionalClassNames, assignment, menuStatus, t } = props;

  const { assignmentManager, dialogManager, userManager } = useContext(MobXProviderContext);

  // cache each component via useMemo to avoid re-mounting on every render
  const FeedbackIconSvg = useMemo(() => SatCoreComponent('FeedbackIconSvg'), []);
  const StudentAssignmentDetailsPopup = useMemo(() => SatCoreComponent('StudentAssignmentDetailsPopup'), []);
  const StudentAssignmentPrimaryActionButton = useMemo(() => SatCoreComponent('StudentAssignmentPrimaryActionButton'), []);
  const StudentAssignmentTitlePopup = useMemo(() => SatCoreComponent('StudentAssignmentTitlePopup'), []);
  const StudentAssignmentTryAgainButton = useMemo(() => SatCoreComponent('StudentAssignmentTryAgainButton'), []);

  if (!assignment || assignment.visibilityMode === 'hidden_from_students') {
    return null;
  }

  const title = StudentAssignmentService.getStudentAssignmentTitle(assignment, {
    preferStudentAssignmentNickname: assignmentManager.useAssignmentNickname
  });
  const parsedTitle = UtilityService.reactHtmlParserWrapper(title).parsed;
  const statusLabelKey = StudentAssignmentService.getStudentAssignmentStatusLabelKey(assignment);
  const { dueDateText, dueDateType, timezoneAbbreviation } = StudentAssignmentService.getStudentAssignmentDueDateLabel(assignment, {
    timezone: userManager.user?.timezone
  });
  const { assignmentGrade, assignmentGradeClassName, assignmentGradeKey } =
    StudentAssignmentService.getStudentAssignmentGradeInfo(assignment);

  const handleFeedback = (activityInstanceId) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.STUDENT_FEEDBACK, {
      assignmentInstanceId: activityInstanceId
    }, () => dialogManager.closeDialog(DIALOG_NAMES.STUDENT_FEEDBACK));
  };

  const renderStudentAssignmentListCard = () => {
    const isClosedOrGraded = menuStatus === ASSIGNMENT_MENU_STATUS.CLOSED
      || menuStatus === ASSIGNMENT_MENU_STATUS.GRADED;
    return (
      <Table.Row className={classNames('StudentAssignmentListCard', additionalClassNames)}>
        {renderStudentAssignmentImage()}
        {renderStudentAssignmentTitleWrapper()}
        {isClosedOrGraded && renderStudentAssignmentSpacer()}
        {!isClosedOrGraded && renderStudentAssignmentStatus()}
        {!isClosedOrGraded && renderStudentAssignmentDueDate()}
        {renderStudentAssignmentDetailsPopup()}
        {renderStudentAssignmentGrade()}
        {isClosedOrGraded && renderStudentAssignmentFeedback()}
        {renderStudentAssignmentActionButtons()}
      </Table.Row>
    );
  };

  const renderStudentAssignmentImage = () => {
    // note: per DEMO-3099, when no image is supplied, the current design (by default)
    // utilizes a solid background color rather than the typical default assignment image.
    // const defaultImg = assignmentManager.customDefaultAssignmentCardImg || defaultCardImage;
    const defaultImg = null;
    return (
      <Table.Cell className='student-assignment-list-card-image'>
        <img
          alt=''
          src={ImageService.getImageUrl(assignment) || defaultImg} />
      </Table.Cell>
    );
  };

  const renderStudentAssignmentTitleWrapper = () => {
    return (
      <Table.Cell className='student-assignment-list-card-title-wrapper'>
        <StudentAssignmentTitlePopup
          assignment={assignment}
          popupOptions={{
            mouseEnterDelay: 1000,
            trigger: (
              <div className='student-assignment-list-card-title-wrapper-inner'>
                {renderStudentAssignmentTitle()}
                {renderStudentAssignmentSubtitleAndCourse()}
              </div>
            )
          }} />
      </Table.Cell>
    );
  };

  const renderStudentAssignmentTitle = () => {
    return (
      <div className='student-assignment-list-card-title'>
        {parsedTitle}
      </div>
    );
  };

  const renderStudentAssignmentSubtitleAndCourse = () => {
    const subtitle = assignment.resourceWebSubtitle;
    const courseName = assignment.courseContentItemFriendlyName || assignment.courseContentItemName;
    if (!subtitle && !courseName) {
      return null;
    }
    const parsedSubtitle = UtilityService.reactHtmlParserWrapper(subtitle).parsed;
    const parsedCourseName = UtilityService.reactHtmlParserWrapper(courseName).parsed;
    return (
      <div className='student-assignment-list-card-subtitle-course'>
        {!!subtitle && (
          <span className='student-assignment-list-card-subtitle'>
            {parsedSubtitle}
          </span>
        )}
        {!!subtitle && !!courseName && (
          <span className='student-assignment-list-card-subtitle-course-separator'>
            |
          </span>
        )}
        {!!courseName && (
          <span className='student-assignment-list-card-course'>
            {parsedCourseName}
          </span>
        )}
      </div>
    );
  };

  const renderStudentAssignmentSpacer = () => {
    return <Table.Cell className='student-assignment-list-card-spacer' />;
  };

  const renderStudentAssignmentStatus = () => {
    return (
      <Table.Cell className='student-assignment-list-card-status'>
        <span className={classNames('status-indicator', statusLabelKey)} />
        {!!statusLabelKey && t(statusLabelKey)}
      </Table.Cell>
    );
  };

  const renderStudentAssignmentDueDate = () => {
    const dueDateLabel = !!dueDateText && t(dueDateType, { dateText: dueDateText, timezoneAbbreviation });
    // when the cell already shows the date (dueSpecificDate), the popup
    // shows just time; otherwise it shows both date and time.
    const showDueDate = dueDateType !== 'dueSpecificDate';
    return (
      <Table.Cell className={classNames('student-assignment-list-card-due-date', dueDateType)}>
        <StudentAssignmentDetailsPopup
          assignment={assignment}
          popupOptions={{
            on: 'hover',
            trigger: <span>{dueDateLabel}</span>
          }}
          showDateAssigned={false}
          showDueDate={showDueDate}
          showSubmitted={false}
          timezoneAbbreviation={timezoneAbbreviation} />
      </Table.Cell>
    );
  };

  const renderStudentAssignmentDetailsPopup = () => {
    return (
      <Table.Cell className='student-assignment-list-card-details'>
        <StudentAssignmentDetailsPopup
          assignment={assignment}
          popupOptions={{
            trigger: (
              <button className='student-assignment-list-card-details-trigger' type='button'>
                {t('details')}
              </button>
            )
          }}
          timezoneAbbreviation={timezoneAbbreviation} />
      </Table.Cell>
    );
  };

  const renderStudentAssignmentGrade = () => {
    // cell always renders to preserve column alignment;
    // content varies by menuStatus when there is no numeric grade.
    //   - Graded tab: 'N/A' for both notAvailable and waitingOnGrade
    //   - Closed tab: 'Waiting on Grade' when waitingOnGrade; '' when notAvailable
    //   - Other tabs: defaults from the namespace (currently empty)
    let gradeLabel;
    if (assignmentGrade) {
      gradeLabel = assignmentGrade;
    } else if (assignmentGradeKey) {
      if (menuStatus === ASSIGNMENT_MENU_STATUS.GRADED) {
        gradeLabel = t('notAvailable');
      } else if (menuStatus === ASSIGNMENT_MENU_STATUS.CLOSED && assignmentGradeKey === 'waitingOnGrade') {
        gradeLabel = t('waitingOnGradeClosed');
      } else if (menuStatus === ASSIGNMENT_MENU_STATUS.CLOSED && assignmentGradeKey === 'notAvailable') {
        gradeLabel = t('notAvailableClosed');
      } else {
        gradeLabel = t(assignmentGradeKey);
      }
    }
    return (
      <Table.Cell className={classNames('student-assignment-list-card-grade', assignmentGradeClassName)}>
        {gradeLabel}
      </Table.Cell>
    );
  };

  const renderStudentAssignmentFeedback = () => {
    // cell always renders to preserve column alignment;
    // icon only renders when the teacher has left feedback.
    return (
      <Table.Cell className='student-assignment-list-card-feedback'>
        {(!!assignment?.teacherFeedback) && (
          <FeedbackIconSvg
            className='student-assignment-list-card-feedback-icon'
            clickHandler={() => handleFeedback(assignment.activityInstanceId)}
            tabIndex={0} />
        )}
      </Table.Cell>
    );
  };

  const renderStudentAssignmentActionButtons = () => {
    return (
      <Table.Cell className='student-assignment-list-card-actions'>
        <div className='student-assignment-list-card-actions-inner'>
          <StudentAssignmentPrimaryActionButton
            assignment={assignment}
            menuStatus={menuStatus} />
          {StudentAssignmentService.isStudentAssignmentTryAgainEligible(assignment) && (
            <StudentAssignmentTryAgainButton assignment={assignment} />
          )}
        </div>
      </Table.Cell>
    );
  };

  return renderStudentAssignmentListCard();
});
export default StudentAssignmentListCard;

SatCoreRegister('StudentAssignmentListCard', StudentAssignmentListCard);
